/**
 * External Imports
 */
import { FC, useEffect, useMemo, useState } from 'react';
import { format, previousSunday, startOfQuarter, sub } from 'date-fns';

/**
 * Nexus Imports
 */
import { NeuInput } from '@neutron/react';

/**
 * Internal Imports
 */
import EmployeeSearch from '../../reusableModalPieces/employeeSearch';
import { Dropdown } from '../../../shared/dropdowns';

import { date as dt } from '../../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import { Employee, Filter } from '../../../../config/interfaces';
import qualifyingEmpRoundingTemplates from '../../../../constants/qualifyingEmpRoundingTemplates.json';

/**
 * Style Imports
 */
import { InputHeading } from '../FilterModal.styles';

const RoundsReportFilterSection: FC<{
  filterValues: Filter;
  type: string;
  roundsReportType?: string;
  setFilterValues: (data: Filter) => void;
  handleRemoveChip: (type: string) => void;
  setIsCensusDateMoreThan7Days: (val: boolean) => void;
}> = ({
  filterValues,
  type,
  roundsReportType,
  setFilterValues,
  handleRemoveChip,
  setIsCensusDateMoreThan7Days
}) => {
  const [reportDateRangeType, setReportDateRangeType] = useState<string>('');
  const [roundedBy, setRoundedBy] = useState<string[]>([]);
  const [showCustomDate, setShowCustomDate] = useState<boolean>(
    filterValues.dateRangeText === 'Custom'
  );
  const [openDateRange, setOpenDateRange] = useState(false);
  const [openTemplate, setOpenTemplate] = useState(false);

  const dateRanges = useMemo(() => {
    if (roundsReportType === 'census') {
      return ['Custom', 'Today', 'Last full week'];
    }
    return [
      'Custom',
      'Today',
      'Month to date',
      'Last full week',
      'Last full month',
      'Last full quarter'
    ];
  }, [roundsReportType]);

  const handleInputs = (e: any) => {
    const { name, value } = e.target as HTMLInputElement;
    return setFilterValues({
      ...filterValues,
      [name]: value
    });
  };

  const handleEmployeeSearchSelected = (employee: Employee) => {
    if (type === 'patient') {
      setFilterValues({
        ...filterValues,
        roundedBy: [...filterValues.roundedBy, employee.employeeId]
      });
    } else {
      setFilterValues({
        ...filterValues,
        employee: `${employee.firstName} ${employee.lastName}` || '',
        employeeId: employee.employeeId
      });
    }
  };

  const handleTemplateSelected = (value: string) => {
    const selectedTemplate = value;
    return selectedTemplate
      ? setFilterValues({
          ...filterValues,
          template: selectedTemplate
        })
      : undefined;
  };

  const handleDateRangeSelect = (value: string) => {
    const selectedRange = value;
    setReportDateRangeType(selectedRange);

    const today = new Date();
    if (selectedRange === 'Today') {
      setShowCustomDate(false);

      const dayToday = format(
        new Date(today.getFullYear(), today.getMonth(), today.getDate()),
        'yyyy-MM-dd'
      );

      setFilterValues({
        ...filterValues,
        startDate: dayToday,
        endDate: dayToday,
        dateRangeText: selectedRange
      });
    }

    if (selectedRange === 'Month to date') {
      setShowCustomDate(false);

      const firstDayOfMonth = format(
        new Date(today.getFullYear(), today.getMonth(), 1),
        'yyyy-MM-dd'
      );

      const dayToday = format(
        new Date(today.getFullYear(), today.getMonth(), today.getDate()),
        'yyyy-MM-dd'
      );

      setFilterValues({
        ...filterValues,
        dateRangeText: selectedRange,
        startDate: firstDayOfMonth,
        endDate: dayToday
      });
    }

    if (selectedRange === 'Last full week') {
      setShowCustomDate(false);
      const lastSunday = previousSunday(new Date());
      const lastSaturdayFormatted = format(
        new Date(
          lastSunday.getFullYear(),
          lastSunday.getMonth(),
          lastSunday.getDate() - 1
        ),
        'yyyy-MM-dd'
      );

      const secondLastSunday = format(
        new Date(
          lastSunday.getFullYear(),
          lastSunday.getMonth(),
          lastSunday.getDate() - 7
        ),
        'yyyy-MM-dd'
      );

      setFilterValues({
        ...filterValues,
        dateRangeText: selectedRange,
        startDate: secondLastSunday,
        endDate: lastSaturdayFormatted
      });
    }

    if (selectedRange === 'Last full month') {
      setShowCustomDate(false);

      const lastMonthFirstDay = format(
        new Date(today.getFullYear(), today.getMonth() - 1, 1),
        'yyyy-MM-dd'
      );

      const lastMonthLastDay = format(
        sub(new Date(today.getFullYear(), today.getMonth(), 1), { days: 1 }),
        'yyyy-MM-dd'
      );
      setFilterValues({
        ...filterValues,
        dateRangeText: selectedRange,
        startDate: lastMonthFirstDay,
        endDate: lastMonthLastDay
      });
    }

    if (selectedRange === 'Last full quarter') {
      setShowCustomDate(false);

      const thisQuarterFirstDay = startOfQuarter(today);
      const lastQuarterLastDayFormatted = format(
        new Date(
          thisQuarterFirstDay.getFullYear(),
          thisQuarterFirstDay.getMonth(),
          thisQuarterFirstDay.getDate() - 1
        ),
        'yyyy-MM-dd'
      );
      const lastQuarterFirstDay = format(
        startOfQuarter(
          new Date(
            thisQuarterFirstDay.getFullYear(),
            thisQuarterFirstDay.getMonth(),
            thisQuarterFirstDay.getDate() - 1
          )
        ),
        'yyyy-MM-dd'
      );
      setFilterValues({
        ...filterValues,
        dateRangeText: selectedRange,
        startDate: lastQuarterFirstDay,
        endDate: lastQuarterLastDayFormatted
      });
    }

    if (selectedRange === 'Custom') {
      setShowCustomDate(true);
      setFilterValues({
        ...filterValues,
        dateRangeText: selectedRange
      });
    }
  };

  const minStartDate = useMemo(
    () => dt.convertToDayOfYear(sub(new Date(), { years: 2 })),
    []
  );
  const maxStartDate = useMemo(() => {
    // for census report, any start date is allowed up to today
    // end date logic will handle the 7 days limit
    if (roundsReportType === 'census') {
      return format(new Date(), 'yyyy-MM-dd');
    }
    return (
      filterValues.endDate &&
      dt.convertToDayOfYear(dt.localDayOfYearDate(filterValues.endDate))
    );
  }, [filterValues.endDate, roundsReportType]);

  const minEndDate = useMemo(() => {
    return (
      filterValues.startDate &&
      dt.convertToDayOfYear(dt.localDayOfYearDate(filterValues.startDate))
    );
  }, [filterValues.startDate]);

  const maxEndDate = useMemo(() => {
    if (roundsReportType === 'census') {
      if (filterValues.startDate) {
        const tempEndDate = dt.convertToDayOfYear(
          dt.localDayOfYearDate(
            dt.addNumDayToDayOfYearDate(filterValues.startDate, 7)
          )
        );
        const today = format(new Date(), 'yyyy-MM-dd');
        return tempEndDate > today ? today : tempEndDate;
      }
    }
    return dt.convertToDayOfYear(new Date());
  }, [filterValues.startDate, roundsReportType]);

  useEffect(() => {
    if (filterValues.dateRangeText === 'Custom') {
      setShowCustomDate(true);
    } else {
      setShowCustomDate(false);
    }
    setReportDateRangeType(filterValues.dateRangeText || 'Today');
    setRoundedBy(filterValues.roundedBy || []);

    // max end date doesn't disable the apply button,
    // so this logic enables/disables it specifically only for census report
    if (
      roundsReportType === 'census' &&
      filterValues.endDate &&
      maxEndDate &&
      minEndDate
    ) {
      if (
        filterValues.endDate >= minEndDate &&
        filterValues.endDate <= maxEndDate
      ) {
        setIsCensusDateMoreThan7Days(false);
      } else {
        setIsCensusDateMoreThan7Days(true);
      }
    }
  }, [filterValues]);

  return (
    <div id="Rounds-Report-Filter-Section">
      <InputHeading>Rounded By</InputHeading>
      <EmployeeSearch
        chipVal={filterValues.employee}
        multipleChipVals={roundedBy}
        showLabel={false}
        val={
          type === 'patient' ? filterValues.roundedBy : filterValues.employee
        }
        handleRemoveEmployee={handleRemoveChip}
        setEmployeeSelection={(emp: Employee) =>
          handleEmployeeSearchSelected(emp)
        }
        isMultipleRounderSelectionAllowed={type === 'patient'}
      />

      {type === 'patient' && (
        <>
          <InputHeading>Date Range</InputHeading>
          <Dropdown
            classes="ps-0"
            name="date-range"
            open={openDateRange}
            placeholder={reportDateRangeType}
            setOpen={setOpenDateRange}
          >
            {dateRanges.map((range: string) => (
              <div
                key={range}
                aria-selected={reportDateRangeType === range}
                className={`dropdown-hover-item${
                  reportDateRangeType === range ? ' selected' : ''
                }`}
                role="option"
                tabIndex={0}
                onClick={
                  reportDateRangeType
                    ? () => handleDateRangeSelect(range)
                    : undefined
                }
                onKeyDown={
                  reportDateRangeType
                    ? () => handleDateRangeSelect(range)
                    : undefined
                }
              >
                {range}
              </div>
            ))}
          </Dropdown>
          {showCustomDate ? (
            <div className="my-1">
              <NeuInput
                id="start-date"
                className="my-1"
                label="Starting"
                name="startDate"
                min={minStartDate}
                max={roundsReportType === 'census' ? minEndDate : maxStartDate}
                type="date"
                value={filterValues.startDate}
                onNeuChange={handleInputs}
              />
              <NeuInput
                id="end-date"
                className="my-1"
                label="Ending"
                name="endDate"
                min={
                  roundsReportType === 'census'
                    ? filterValues.startDate
                    : minEndDate
                }
                max={maxEndDate}
                type="date"
                value={filterValues.endDate}
                onNeuChange={handleInputs}
              />
            </div>
          ) : null}
        </>
      )}
      {type === 'employee' && (
        <>
          <InputHeading>Round Type</InputHeading>
          <Dropdown
            classes="ps-0"
            open={openTemplate}
            placeholder={filterValues.template || 'Select a rounding template'}
            setOpen={setOpenTemplate}
          >
            {qualifyingEmpRoundingTemplates.map((template: string) => (
              <div
                aria-selected={filterValues.template === template}
                className={`dropdown-hover-item${
                  filterValues.template === template ? ' selected' : ''
                }`}
                key={template}
                role="option"
                tabIndex={0}
                onClick={() => handleTemplateSelected(template)}
                onKeyDown={() => handleTemplateSelected(template)}
              >
                {template}
              </div>
            ))}
          </Dropdown>
        </>
      )}
    </div>
  );
};

export default RoundsReportFilterSection;
